import { useId, type ReactNode } from "react";
import type { EmailConversationRow } from "@gioia365/contracts";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { DataTable } from "../design/components/DataTable";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { Select } from "../design/components/Select";
import { formatNumericDateTime } from "../i18n/format";
import { useT } from "../i18n/locale";
import { useResultSearch } from "../leads/lead-list-hooks";
import { useLeadView } from "../leads/lead-queries";
import { canActOnLeadOf, conversationScopeOf, useAccess } from "../session/access";
import { ConversationsPeriod, ConversationsProblem, ConversationsSearchField, type ConversationsTabProps } from "./ConversationParts";
import { conversationPeopleQuery } from "./conversation-queries";
import { emailListQueryOf } from "./conversation-search";
import { emailPagesQuery } from "./email-queries";

// La scheda «Email» di «Conversazioni» (piano dell'ingresso del 12 ottobre 2026, I5, D5; INGR-22): gli eventi delle email di
// GoHighLevel dei Lead nel perimetro di chi guarda (`GET /api/conversations/email`: dei Lead di cui è owner; con «tutti» tutti; nella
// «Guarda come…» quelli della persona osservata), dal più recente, a pagine, in sola lettura (le email le manda GoHighLevel, PROD-3).
// Periodo, «Di chi» (soltanto per chi vede tutti) e la ricerca stanno nell'indirizzo, come nelle altre schede. Le colonne: «Quando»,
// «Lead» (con il salone), «Evento» (le parole della Timeline) e «Oggetto», con il link sotto (si apre in una scheda nuova). Sopra, una
// nota dice che un'apertura può essere l'anteprima automatica di un programma di posta. Sotto i 720 px ogni riga è una scheda.

function EmailRowView({ row, timeZone, canOpenLead }: { row: EmailConversationRow; timeZone: string; canOpenLead: boolean }) {
  const t = useT();
  const { event, lead } = row;
  return (
    <tr>
      <td data-label={t("conversations.email.columns.when")}>{formatNumericDateTime(timeZone, event.at)}</td>
      <th scope="row">
        {canOpenLead ? (
          <Link to="/lead/$leadId" params={{ leadId: lead.id }}>
            {lead.displayName ?? "—"}
          </Link>
        ) : (
          <span className="g-conversations__lead">{lead.displayName ?? "—"}</span>
        )}
        {lead.salonName ? <span className="g-table__sub">{lead.salonName}</span> : null}
      </th>
      <td data-label={t("conversations.email.columns.event")}>
        <span className="g-pill" data-tone={event.type === "clicked" || event.type === "replied" ? "accent" : "neutral"}>
          <Icon name="mail" />
          {t(`timeline.email.events.${event.type}`)}
        </span>
      </td>
      <td data-label={t("conversations.email.columns.subject")}>
        {event.subject !== null ? <span className="g-conversations__message">{t("conversations.email.quoted", { text: event.subject })}</span> : "—"}
        {event.url !== null ? (
          <span className="g-table__sub g-conversations__message">
            {t("conversations.email.link")}{" "}
            <a href={event.url} target="_blank" rel="noopener noreferrer">
              {event.url}
            </a>
          </span>
        ) : null}
      </td>
    </tr>
  );
}

export function EmailTab({ search, update, writeQ }: ConversationsTabProps) {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const view = useLeadView();
  const baseId = useId();
  // «Di chi» soltanto per chi vede le conversazioni di tutti, con la regola del server.
  const byPerson = conversationScopeOf(access) === "all";
  const query = emailListQueryOf(search, byPerson);
  const pages = useInfiniteQuery({ ...emailPagesQuery(tab, query, view), placeholderData: keepPreviousData });
  const people = useQuery(conversationPeopleQuery(tab));
  const timeZone = access.context.settings.timeZone;
  const shown = pages.data && !pages.isPlaceholderData ? pages.data.pages.flatMap((page) => page.rows) : null;
  const { text, setText } = useResultSearch(search.q, writeQ);

  const peopleOptions = (people.data ?? []).map((person) => ({ value: person.membershipId, label: person.displayName }));
  // Una persona dell'indirizzo che non è fra le scelte (un link di prima) resta scelta, con il nome che le righe le danno.
  const whoOptions = [{ value: "all", label: t("conversations.wholeTeam") }, ...peopleOptions];
  if (search.chi !== undefined && !whoOptions.some((option) => option.value === search.chi)) {
    const named = shown?.find((row) => row.lead.currentOwner?.membershipId === search.chi)?.lead.currentOwner?.displayName;
    whoOptions.push({ value: search.chi, label: named ?? search.chi });
  }

  let results: ReactNode;
  if (shown === null) {
    results = pages.isError ? (
      <ConversationsProblem error={pages.error} onRetry={() => void pages.refetch()} />
    ) : (
      <p className="g-conversations__loading" role="status">
        {t("common.loading")}
      </p>
    );
  } else if (shown.length === 0) {
    results = (
      <div className="g-conversations__empty">
        <p>
          <strong>{t("conversations.email.empty")}</strong>
        </p>
        <p>{t("conversations.email.emptyHint")}</p>
      </div>
    );
  } else {
    results = (
      <>
        {pages.isError ? <ConversationsProblem error={pages.error} onRetry={() => void pages.refetch()} /> : null}
        <Reason>{t("conversations.email.note")}</Reason>
        <div className="g-conversations__table g-conversations__email">
          <DataTable caption={t("conversations.email.caption")}>
            <thead>
              <tr>
                <th scope="col">{t("conversations.email.columns.when")}</th>
                <th scope="col">{t("conversations.email.columns.lead")}</th>
                <th scope="col">{t("conversations.email.columns.event")}</th>
                <th scope="col">{t("conversations.email.columns.subject")}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <EmailRowView
                  key={row.event.id}
                  row={row}
                  timeZone={timeZone}
                  // Il Lead si apre con le regole del server (`canActOnLead`), come nelle altre schede.
                  canOpenLead={canActOnLeadOf(access, "view", row.lead.currentOwner?.membershipId ?? null)}
                />
              ))}
            </tbody>
          </DataTable>
        </div>
        {pages.hasNextPage ? (
          <div>
            <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>
              {t("conversations.email.more")}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <div className="g-conversations__toolbar">
        <ConversationsPeriod search={search} update={update} />
        <div className="g-conversations__filters">
          {byPerson ? (
            <Select id={`${baseId}-who`} label={t("conversations.who")} value={search.chi ?? "all"} options={whoOptions} onChange={(value) => update({ chi: value === "all" ? undefined : value })} />
          ) : null}
        </div>
        <ConversationsSearchField id={`${baseId}-search`} text={text} onChange={setText} />
      </div>
      <div className="g-conversations__results" aria-busy={shown === null && !pages.isError ? true : undefined}>
        {results}
      </div>
    </>
  );
}
